import React, { useRef, useState } from 'react'
import { LuMoreVertical } from 'react-icons/lu'
import {
  MdFormatAlignCenter, MdFormatAlignJustify,
  MdFormatAlignLeft, MdFormatAlignRight,
  MdFormatBold,
  MdFormatItalic,
  MdFormatListBulleted,
  MdFormatListNumbered,
  MdFormatQuote,
  MdFormatUnderlined, MdTitle
} from 'react-icons/md'
import { useMeasure } from 'react-use'

import { useClickOutside } from '../../../hooks'
import { StatusSelector } from '../../PatientFolder/behavioral/components'
import { MaterialDeleteButton } from '../../shared/material-delete-button'

import { BlockButton } from './BlockButton'
import { ButtonGroup } from './ButtonGroup'
import { CheckboxListButton } from './CheckboxListButton'
import { InlineCheckboxButton } from './InlineCheckboxButton'
import { MarkButton } from './MarkButton'
import { BlockButtonInSelect, InlineButtonInSelect, CheckboxListButtonInSelect, MarkButtonInSelect } from './select'

const elements = [
  [{
    mark: true,
    format: 'bold',
    icon: <MdFormatBold size='1.2rem' />,
    label: 'Bold'
  }, {
    mark: true,
    format: 'italic',
    icon: <MdFormatItalic size='1.2rem' />,
    label: 'Italic'
  }, {
    mark: true,
    format: 'underline',
    icon: <MdFormatUnderlined size='1.2rem' />,
    label: 'Underline'
  }],
  [
    {
      format: 'heading-two',
      icon: <MdTitle size='1.2rem' />,
      label: 'Heading'
    }, {
      format: 'block-quote',
      icon: <MdFormatQuote size='1.2rem' />,
      label: 'Quote'
    }
  ],
  [
    {
      format: 'ordered-list',
      icon: <MdFormatListNumbered size='1.2rem' />,
      label: 'Ordered list'
    },
    {
      format: 'unordered-list',
      icon: <MdFormatListBulleted size='1.2rem' />,
      label: 'Unordered list'
    }
  ],
  [
    {
      format: 'left',
      icon: <MdFormatAlignLeft size='1.2rem' />,
      label: 'Left-aligned paragraph'
    },
    {
      format: 'center',
      icon: <MdFormatAlignCenter size='1.2rem' />,
      label: 'Center-aligned paragraph'
    },
    {
      format: 'right',
      icon: <MdFormatAlignRight size='1.2rem' />,
      label: 'Right-aligned paragraph'
    },
    {
      format: 'justify',
      icon: <MdFormatAlignJustify size='1.2rem' />,
      label: 'Justified paragraph'
    }
  ],
  {
    children: [
      <InlineCheckboxButton key='inline-checkbox-button' />,
      <CheckboxListButton key='checkbox-list-button' />
    ]
  }
]

export function Menu ({
  isReadOnly,
  actions,
  statuses,
  selectedStatus
}) {
  const [ ref, { width }] = useMeasure()

  const { onDelete, onChangeStatus } = actions

  let lastElementIndex

  if (width <= 140) {
    lastElementIndex = 0
  }

  if (width >= 140) {
    lastElementIndex = 1
  }

  if (width >= 190) {
    lastElementIndex = 2
  }

  if (width >= 260) {
    lastElementIndex = 3
  }
  if (width >= 380) {
    lastElementIndex = 4
  }
  if (width >= 420) {
    lastElementIndex = 5
  }

  if (isReadOnly) {
    return
  }

  return (
    <div className='flex flex-row items-center sticky top-0 z-30 bg-white border-b border-b-gray-200 py-6 px-6 h-16 justify-between'>
      <div
        className='flex flex-row gap-6 w-full'
        ref={ref}
      >
        {elements.slice(0, lastElementIndex).map((headerElement, index) => {
          if (Array.isArray(headerElement)) {
            return (
              <ButtonGroup key={index}>
                {headerElement.map((el) => {
                  if (el.mark) {
                    return (
                      <MarkButton
                        key={el.label}
                        format={el.format}
                        label={el.label}
                        icon={el.icon}
                      />
                    )
                  }

                  return (
                    <BlockButton
                      key={el.label}
                      format={el.format}
                      label={el.label}
                      icon={el.icon}
                    />
                  )
                })}
              </ButtonGroup>
            )
          }

          return (
            <ButtonGroup key={index}>
              {headerElement.children}
            </ButtonGroup>
          )
        })}

        {lastElementIndex !== 5 && (
          <MoreOptions options={elements.slice(lastElementIndex)} />
        )}
      </div>

      <div className='flex flex-row gap-4 items-center'>
        <StatusSelector
          onChange={onChangeStatus}
          availableStatuses={statuses}
          activeStatusId={selectedStatus}
        />
        {typeof onDelete === 'function' && (
          <MaterialDeleteButton
            onClick={onDelete}
            inTable={false}
          />
        )}
      </div>
    </div>
  )
}

export function MoreOptions ({ options }) {
  const [ open, setOpen ] = useState(false)
  const ref = useRef()

  useClickOutside(ref, () => {
    setOpen(false)
  })

  return (
    <div
      className='relative'
      ref={ref}
    >
      <button onClick={() => { setOpen(true) }}>
        <LuMoreVertical size='1rem' />
      </button>
      {open && (
        <div
          className='absolute -bottom-2 -translate-x-1/2 translate-y-full bg-white  rounded-md z-20 divide-y divide-gray-300 w-48 '
          style={{ boxShadow: '0 0 0 1px hsla(0, 0%, 0%, 0.1), 0 4px 11px hsla(0, 0%, 0%, 0.1)' }}
        >
          {options.map((elementGroup) => {
            if (Array.isArray(elementGroup)) {
              return elementGroup.map((el) => {
                if (el.mark) {
                  return (
                    <MarkButtonInSelect
                      key={el.label}
                      format={el.format}
                      label={el.label}
                      icon={el.icon}
                    />
                  )
                }
                return (
                  <BlockButtonInSelect
                    key={el.label}
                    format={el.format}
                    label={el.label}
                    icon={el.icon}
                  />
                )
              })
            }

            return [
              <InlineButtonInSelect key='inline-button-in-select' />,
              <CheckboxListButtonInSelect key='checkbox-list-button-in-select' />
            ]
          })}
        </div>
      )}
    </div>
  )
}
